import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  DocumentCheckIcon,
  MapPinIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import hospital from '~/assets/landing/hospital.webp';
import provider from '~/assets/landing/provider.webp';
import { CheckList } from './LandingElements';

const MarketplaceCard = ({ organization = false }: { organization?: boolean }) => (
  <div
    className="landing-marketplace-card"
    role="img"
    aria-label={
      organization
        ? 'Example clinical service listing: Radiology Coverage, remote, 12 FTEs, $240 per hour.'
        : 'Example opportunity: Emergency Medicine, Arizona, onsite, $160 per hour.'
    }
  >
    <img
      className={organization ? 'landing-marketplace-photo--hospital' : undefined}
      src={organization ? hospital : provider}
      alt=""
      width="150"
      height="100"
      loading="lazy"
    />
    <div>
      <h4>{organization ? 'Radiology Coverage' : 'Emergency Medicine'}</h4>
      <span className="landing-listing-type">{organization ? 'Renewable' : 'Locum tenens'}</span>
      <p>Provider Type: Physician</p>
      <p>Specialty: {organization ? 'Diagnostic Radiology' : 'Emergency Medicine'}</p>
      <div className="landing-listing-facts">
        <span>
          <MapPinIcon aria-hidden="true" />
          {organization ? '4 states' : 'AZ'}
        </span>
        <span>
          <BuildingOffice2Icon aria-hidden="true" />
          {organization ? 'Remote' : 'Onsite'}
        </span>
        <span>
          <CalendarDaysIcon aria-hidden="true" />
          Jan 5, 2027
        </span>
        <span>
          <UserGroupIcon aria-hidden="true" />
          {organization ? '12 FTEs' : '10.0 hrs/shift'}
        </span>
      </div>
      <strong className="landing-listing-rate">{organization ? '$240/hr' : '$160/hr'}</strong>
      {organization && <span className="landing-listing-total">$4,608,000 total</span>}
    </div>
  </div>
);

const matchingFactors = [
  { lines: ['Specialty &', 'Subspecialty'], icon: UserGroupIcon },
  { lines: ['Licensure &', 'Certifications'], icon: DocumentCheckIcon },
  { lines: ['Availability &', 'Schedule'], icon: CalendarDaysIcon },
  { lines: ['Location &', 'Travel'], icon: MapPinIcon },
  { lines: ['Compensation &', 'Contract Terms'], icon: CurrencyDollarIcon },
  { lines: ['Organizational', 'Requirements'], icon: BuildingOffice2Icon },
];

const MatchingPreview = () => (
  <div className="landing-matching-preview">
    <ul>
      {matchingFactors.map(({ lines, icon: Icon }) => (
        <li key={lines.join(' ')}>
          <span>
            <Icon aria-hidden="true" />
          </span>
          <div className="landing-matching-label">
            {lines.map((line, index) => (
              <span key={line}>{index > 0 ? ` ${line}` : line}</span>
            ))}
          </div>
        </li>
      ))}
    </ul>
    {/* [MCV-1881] The rule across the six factors converges on a point over the result. */}
    <div className="landing-matching-divider" aria-hidden="true">
      <span />
      <svg viewBox="0 0 32 16" focusable="false">
        <polyline points="0,1 16,15 32,1" />
      </svg>
      <span />
    </div>
    <div className="landing-matching-result">More Relevant Workforce Matches</div>
  </div>
);

const CapacityPreview = () => (
  <div className="landing-capacity-preview">
    <div>
      <UserGroupIcon aria-hidden="true" />
      <h4>Physician Group</h4>
      <p>Publishes available specialty coverage</p>
    </div>
    <ArrowRightIcon className="landing-flow-arrow" aria-hidden="true" />
    <div>
      <UserGroupIcon aria-hidden="true" />
      <h4>Healthcare Labor Exchange</h4>
      <p>
        Makes the capacity searchable by requirements, availability, location, and contract
        structure
      </p>
    </div>
    <ArrowRightIcon className="landing-flow-arrow" aria-hidden="true" />
    <div>
      <BuildingOffice2Icon aria-hidden="true" />
      <h4>Hospital or Health System</h4>
      <p>Discovers and evaluates a potential clinical services partner</p>
    </div>
  </div>
);

const availability = [
  { label: 'Available', className: 'available' },
  { label: 'Held', className: 'held' },
  { label: 'Booked', className: 'booked' },
  { label: 'Unavailable', className: 'unavailable' },
];
const sampleEvents: Record<number, number> = {
  5: 0,
  6: 0,
  7: 2,
  8: 2,
  12: 1,
  13: 0,
  14: 0,
  15: 0,
  19: 3,
  20: 3,
  25: 2,
  26: 0,
};

const CalendarPreview = () => (
  <div className="landing-calendar-preview">
    <ul className="landing-calendar-legend" aria-label="Availability legend">
      {availability.map(({ label, className }) => (
        <li className={className} key={label}>
          {label}
        </li>
      ))}
    </ul>
    <table>
      <caption>
        January 2027 <span>Example availability</span>
      </caption>
      <thead>
        <tr>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <th scope="col" key={day}>
              {day}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 6 }, (_, week) => (
          <tr key={week}>
            {Array.from({ length: 7 }, (_, weekday) => {
              const date = week * 7 + weekday - 4;
              const event =
                sampleEvents[date] === undefined ? undefined : availability[sampleEvents[date]];
              return (
                <td key={weekday}>
                  {date > 0 && date <= 31 && (
                    <>
                      <span>{date}</span>
                      {event && <span className={event.className}>{event.label}</span>}
                    </>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const HlxSections = () => (
  <>
    <section className="landing-marketplaces" aria-labelledby="landing-marketplaces-title">
      <div className="landing-container">
        <h2 id="landing-marketplaces-title">Two Marketplaces. One Exchange.</h2>
        <div className="landing-marketplace-grid">
          <div className="landing-marketplace">
            <div>
              <h3>B2C Marketplace</h3>
              <p className="landing-marketplace-subtitle">For Individual Professionals</p>
              <CheckList
                items={[
                  'Discover opportunities that match your specialty, location, and schedule',
                  'Show your availability through an integrated calendar',
                  'Pursue employment, locum tenens, and flexible work arrangements',
                ]}
              />
            </div>
            <MarketplaceCard />
          </div>
          <div className="landing-marketplace">
            <div>
              <h3>B2B Marketplace</h3>
              <p className="landing-marketplace-subtitle">For Organizations</p>
              <CheckList
                items={[
                  'Market available clinical capacity',
                  'Find qualified professionals and clinical service partners',
                  'Pursue contracts from single shifts to comprehensive service agreements',
                ]}
              />
            </div>
            <MarketplaceCard organization />
          </div>
        </div>
      </div>
    </section>
    <section className="landing-how landing-container" aria-labelledby="landing-how-title">
      <h2 id="landing-how-title">How HLX Works</h2>
      <p className="landing-how-intro">
        HLX combines AI-powered matching, workforce availability, and a connected marketplace to
        help healthcare professionals and organizations discover relevant opportunities, evaluate
        potential partners, and exchange clinical services more efficiently.
      </p>
      <ol>
        <li>
          <div>
            <div className="landing-step-heading">
              <span>1</span>
              <h3>AI-Powered Search Across 50,000+ Variables</h3>
            </div>
            <p>
              Healthcare workforce matching is inherently multidimensional. HLX uses AI to analyze
              more than 50,000 variables to identify highly relevant opportunities, qualified
              professionals, and potential clinical service partners.
            </p>
          </div>
          <MatchingPreview />
        </li>
        <li>
          <div>
            <div className="landing-step-heading">
              <span>2</span>
              <h3>Organizations Can Market Clinical Capacity</h3>
            </div>
            <p>
              Physician groups, staffing organizations, and other providers can publish available
              clinical services, from defined shifts to comprehensive coverage arrangements.
            </p>
          </div>
          <CapacityPreview />
        </li>
        <li>
          <div>
            <div className="landing-step-heading">
              <span>3</span>
              <h3>Calendar-Based Availability</h3>
            </div>
            <p>
              Integrated calendar functionality makes real-time and future availability visible,
              helping align workforce capacity with actual market demand.
            </p>
          </div>
          <CalendarPreview />
        </li>
      </ol>
    </section>
  </>
);
